import * as React from 'react';
import { toast } from 'react-toastify';
import { useObservable } from 'react-use-observable';

import { CopyOutlined, LinkOutlined } from '@ant-design/icons';
import { Button, Table } from 'antd';

import { CHECKOUT_ELEMENTS_URL } from 'settings';
import Truncate from 'v2/components/Shared/TruncateText';
import type { ProductOffer } from 'v2/interfaces/product';
import eduzzAccountsService from 'v2/services/eduzzAccount';

type PricesListProps = {
  isAffiliate: boolean;
  offer: ProductOffer;
};

type ProductPrice = ProductOffer['prices'][0];

function PricesList({ isAffiliate, offer }: PricesListProps) {
  const [user] = useObservable(() => eduzzAccountsService.getUser(), []);

  function mountOfferLink(price: ProductPrice) {
    const url = new URL(`${CHECKOUT_ELEMENTS_URL}/${offer.key}?currency=${price.currency.code}`);

    if (isAffiliate && user) {
      url.searchParams.append('a', String(user.id));
    }

    return String(url);
  }

  function copyLinkToClipboard(link: string) {
    navigator.clipboard.writeText(link);
    toast.success('Link copiado para a área de transferência');
  }

  function mountOfferCurrency(price: ProductPrice) {
    return `${price.currency.code} (${price.currency.name})`;
  }

  if (!offer.prices?.length) return null;

  const columns = [
    {
      title: 'Moeda',
      key: 'currency',
      render: (_: any, price: ProductPrice) => (
        <span id={`links-modal-offer-currency-${price.currency.code}`} className='whitespace-nowrap'>
          <Truncate count={25}>{mountOfferCurrency(price)}</Truncate>
        </span>
      )
    },
    {
      title: 'Valor',
      key: 'value',
      width: 112,
      render: (_: any, price: ProductPrice) => (
        <span id={`links-modal-offer-price-${price.currency.code}`}>
          {price.value.toLocaleString('pt-BR', {
            style: 'currency',
            currency: price.currency.code
          })}
        </span>
      )
    },
    {
      title: 'Página de pagamento (Checkout Sun)',
      key: 'link',
      width: 300,
      render: (_: any, price: ProductPrice) => (
        <div className='border-border flex items-center gap-2 border px-2 py-1 pl-[13px]'>
          <LinkOutlined style={{ fontSize: 18 }} />
          <a
            id={`links-modal-offer-link-${price.currency.code}`}
            href={mountOfferLink(price)}
            target='_blank'
            rel='noreferrer'
            className='max-w-[203px] overflow-hidden text-sm leading-6 text-ellipsis whitespace-nowrap text-black hover:underline'
          >
            {mountOfferLink(price)}
          </a>
          <Button
            type='text'
            size='small'
            id={`links-modal-copy-button-${price.currency.code}`}
            onClick={() => copyLinkToClipboard(mountOfferLink(price))}
            icon={<CopyOutlined style={{ fontSize: 18 }} />}
          />
        </div>
      )
    }
  ];

  return <Table columns={columns} dataSource={offer.prices} rowKey='id' pagination={false} size='small' />;
}

export { PricesList };
